import type { OverviewEntity } from "../../modules/overviewAnalysis/types";
import { countryEntity, isOverviewEntity } from "../../modules/overviewAnalysis/entities.mjs";
import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from "react";
import styled, { keyframes } from "styled-components";
import { Cell, Pie, PieChart, Sector, Tooltip, type PieSectorShapeProps } from "recharts";
import { ChartPie, CreditCard, Globe2, Info, Landmark, Lightbulb, Mail } from "lucide-react";
import { useTranslation } from "react-i18next";
import { bankCount, bankCountryName, declineShare } from "./bankDeclineData";
import type { BankAnalysisDetailsData, BankBreakdownRow } from "./bankDeclineData";
import { analyzeRepeats, repeatPolicy } from "./bankRepeatAnalysis";
import type { RepeatSeverity } from "./bankAnalysisConfig";
import { CountryFlag } from "src/view/conponents/icons/CountryFlag";
const Summary = styled.div`
	display: flex; align-items: flex-start; gap: 12px;
	margin: 0 0 18px; padding: 16px; border: 1px solid #c7d7fe; border-radius: 12px; background: #eff4ff;
	> svg { flex-shrink: 0; color: #175cd3; margin-top: 2px; }
	p { margin: 0; line-height: 1.6; font-size: 14px; color: #344054; }
	p + p { margin-top: 6px; font-size: 12px; color: #667085; }
`;
const Charts = styled.div`
	display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 340px), 1fr)); gap: 16px; align-items: start;
`;
const Section = styled.section`
	min-width: 0; padding: 18px; border: 1px solid #e7ebf1; border-radius: 14px; background: #fff;
	h3 { display: flex; align-items: center; gap: 8px; margin: 0; font-size: 14px; color: #344054; }
	h3 svg { color: #7b88a1; }
	@media (max-width: 480px) { padding: 12px; }
`;
const Chart = styled.div`
	position: relative; width: 180px; height: 180px; margin: 12px auto;
`;
function HighlightedSector({ highlighted, muted, ...sector }: PieSectorShapeProps & { highlighted: boolean; muted: boolean }) {
	const [appearance, setAppearance] = useState({ extraRadius: 0, opacity: 1 });
	const current = useRef(appearance);
	useEffect(() => {
		const from = current.current;
		const target = { extraRadius: highlighted ? 6 : 0, opacity: muted ? .4 : 1 };
		const update = (value: typeof target) => {
			current.current = value;
			setAppearance(value);
		};
		if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
			update(target);
			return;
		}
		if (from.extraRadius === target.extraRadius && from.opacity === target.opacity) return;
		const started = performance.now();
		let frame: number;
		const animate = (now: number) => {
			const progress = Math.min((now - started) / 240, 1);
			const eased = 1 - (1 - progress) ** 3;
			update({
				extraRadius: from.extraRadius + (target.extraRadius - from.extraRadius) * eased,
				opacity: from.opacity + (target.opacity - from.opacity) * eased,
			});
			if (progress < 1) frame = requestAnimationFrame(animate);
		};
		frame = requestAnimationFrame(animate);
		// Continue from the last rendered frame when the pointer moves between rows.
		return () => cancelAnimationFrame(frame);
	}, [highlighted, muted]);
	return <Sector cx={sector.cx} cy={sector.cy} innerRadius={sector.innerRadius}
		outerRadius={(sector.outerRadius ?? 0) + appearance.extraRadius} opacity={appearance.opacity}
		startAngle={sector.startAngle} endAngle={sector.endAngle} fill={sector.fill} stroke={sector.stroke} />;
}
const Center = styled.div`
	position: absolute; inset: 0; pointer-events: none; display: flex; flex-direction: column; align-items: center; justify-content: center;
	strong { max-width: 104px; font-size: 23px; font-variant-numeric: tabular-nums; color: #344054; overflow-wrap: anywhere; text-align: center; }
	span { margin-top: 3px; font-size: 11px; color: #98a2b3; }
`;
const tooltipAppear = keyframes`
	from { opacity: 0; transform: translateY(4px); }
	to { opacity: 1; transform: translateY(0); }
`;
const DonutTooltip = styled.div`
	position: fixed; animation: ${tooltipAppear} 180ms ease-out both;
	box-sizing: border-box; width: min(360px, calc(100vw - 64px)); padding: 16px 18px;
	border: 1px solid #d4dce8; border-radius: 12px;
	background: #fff; color: #1d2939; box-shadow: 0 8px 28px #18263b24;
	white-space: normal; overflow-wrap: anywhere; font-size: 14px; line-height: 1.5;
	.tooltip-title { display: flex; align-items: baseline; gap: 9px; font-weight: 600; }
	.tooltip-metrics { display: flex; justify-content: space-between; gap: 20px; margin-top: 12px; padding-top: 12px; border-top: 1px solid #e7ebf1; }
	.tooltip-metrics > div { display: flex; flex-direction: column; gap: 2px; }
	.tooltip-metrics > div:last-child { text-align: right; }
	.tooltip-metrics strong { font-size: 16px; font-weight: 600; font-variant-numeric: tabular-nums; }
	.tooltip-metrics span { font-size: 12px; color: #475467; }
	@media (prefers-reduced-motion: reduce) { animation: none; }
`;
const LegendLabel = styled.span`
	display: flex; align-items: center; gap: 8px;
`;
const Dot = styled.span<{ $color: string }>`
	width: 8px; height: 8px; flex: 0 0 8px; border-radius: 50%; background: ${p => p.$color};
`;
const Table = styled.table`
	width: 100%; border-collapse: collapse; table-layout: fixed; font-size: 13px;
	col:nth-child(2) { width: 76px; }
	col:nth-child(3) { width: 62px; }
	thead { position: absolute; width: 1px; height: 1px; padding: 0; overflow: hidden; clip-path: inset(50%); white-space: nowrap; }
	td { padding: 9px 6px; border-bottom: 1px solid #edf0f4; vertical-align: top; }
	td:first-child { text-align: left; overflow-wrap: anywhere; }
	td:not(:first-child) { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
	td { line-height: 1.45; color: #475467; }
	td:last-child { color: #667085; }
	td:nth-child(2) { font-weight: 600; }
	td small { font-size: 11px; font-weight: 400; color: #98a2b3; margin-left: 4px; }
	tbody tr:last-child td { border-bottom: 0; }
	tbody tr { transition: background-color 200ms ease-out; }
	tbody tr[data-highlighted="true"] { background: #f2f5fb; }
	tbody tr:focus-visible { outline: 2px solid #6374d9; outline-offset: -2px; }
	@media (prefers-reduced-motion: reduce) { tbody tr { transition: none; } }
	@media (max-width: 480px) { col:nth-child(2) { width: 70px; } col:nth-child(3) { width: 56px; } }
`;
const More = styled.button`
	border: 0; padding: 10px 0; background: transparent; color: #365f8c; cursor: pointer; font: inherit; font-size: 13px;
	td & { padding: 0; text-align: left; overflow-wrap: anywhere; }
	&:hover { color: #223f70; text-decoration: underline; }
	&:focus-visible { outline: 2px solid #526782; outline-offset: 2px; }
`;
const Details = styled.details`
	margin-top: 12px; padding: 14px 16px; border: 1px solid #e7ebf1; border-radius: 12px;
	summary { cursor: pointer; font-size: 13px; font-weight: 500; overflow-wrap: anywhere; color: #475467; }
	summary svg { vertical-align: -3px; margin: 0 8px 0 4px; color: #7b88a1; }
	&[open] summary { margin-bottom: 12px; }
	summary:focus-visible { outline: 2px solid #526782; outline-offset: 4px; }
`;
const Note = styled.p`
	margin: 10px 0 0; color: #667085; font-size: 12px; line-height: 1.5;
`;
const AttentionBadge = styled.span<{ $severity: RepeatSeverity }>`
	display: inline-block; padding: 3px 7px; border-radius: 6px; font-size: 12px; font-weight: 600;
	color: ${p => p.$severity === "critical" ? "#b42332" : "#946200"};
	background: ${p => p.$severity === "critical" ? "#fff0f0" : "#fff5d6"};
`;

type ChartRow = BankBreakdownRow & { color: string; aggregate?: boolean };
const COLORS = ["#6374d9", "#49a99e", "#e7b15a", "#b48bd3", "#dd8693"];

const appLanguage = (language: string): "en" | "ru" => language.startsWith("ru") ? "ru" : "en";

function FloatingDonutTooltip({ anchor, name, color, count, total }: { anchor: RefObject<HTMLDivElement>; name: string; color: string; count: number; total: number }) {
	const { t, i18n } = useTranslation();
	const language = appLanguage(i18n.resolvedLanguage || i18n.language);
	const tooltip = useRef<HTMLDivElement>(null);
	const [position, setPosition] = useState<{ left: number; top: number } | null>(null);
	useLayoutEffect(() => {
		const chart = anchor.current, element = tooltip.current;
		if (!chart || !element) return;
		let intersects = true;
		const updatePosition = () => {
			const rect = chart.getBoundingClientRect();
			const width = document.documentElement.clientWidth, height = window.innerHeight;
			if (!intersects || rect.bottom <= 0 || rect.top >= height || rect.right <= 0 || rect.left >= width) {
				setPosition(null);
				return;
			}
			const gap = 12, tooltipWidth = element.offsetWidth, tooltipHeight = element.offsetHeight;
			const left = Math.max(gap, Math.min(rect.left + rect.width / 2 - tooltipWidth / 2, width - tooltipWidth - gap));
			const aboveTop = rect.top - tooltipHeight - gap;
			const preferredTop = aboveTop >= gap ? aboveTop : rect.bottom + gap;
			const top = Math.max(gap, Math.min(preferredTop, height - tooltipHeight - gap));
			setPosition({ left, top });
		};
		updatePosition();
		const observer = new IntersectionObserver(([entry]) => { intersects = entry.isIntersecting; updatePosition(); });
		observer.observe(chart);
		window.addEventListener("scroll", updatePosition, true);
		window.addEventListener("resize", updatePosition);
		return () => {
			observer.disconnect();
			window.removeEventListener("scroll", updatePosition, true);
			window.removeEventListener("resize", updatePosition);
		};
	}, [anchor, name, count, total]);
	return <DonutTooltip ref={tooltip} role="tooltip" style={{ left: position?.left ?? 0, top: position?.top ?? 0, visibility: position ? "inherit" : "hidden" }}>
		<div className="tooltip-title"><Dot $color={color} aria-hidden="true" /><span>{name}</span></div>
		<div className="tooltip-metrics">
			<div><strong>{bankCount(count, language)}</strong><span>{t("bankAnalysis.declines")}</span></div>
			<div><strong>{declineShare(count, total, language)}</strong><span>{t("bankAnalysis.shareOfDeclines")}</span></div>
		</div>
	</DonutTooltip>;
}

function Breakdown({ rows, total, label, entityType, onEntity, attention, highlightedRow, onRowHover, onRowFocus }: { rows: (BankBreakdownRow & Partial<ChartRow>)[]; total: number; label: string; entityType?: "country" | "email" | "bank"; onEntity?: (entity: OverviewEntity) => void; attention?: NonNullable<ReturnType<typeof repeatPolicy>>; highlightedRow?: number | null; onRowHover?: (index: number | null) => void; onRowFocus?: (index: number | null) => void }) {
	const { t, i18n } = useTranslation();
	const language = appLanguage(i18n.resolvedLanguage || i18n.language);
	return <Table aria-label={label}>
		<colgroup><col /><col /><col /></colgroup>
		<thead><tr><th scope="col">{label}</th><th scope="col">{t("bankAnalysis.declines")}</th><th scope="col">{t("bankAnalysis.shareOfDeclines")}</th></tr></thead>
		<tbody>{rows.map((row, index) => {
   const entity = row.aggregate || row.unknown ? undefined : entityType === "country" ? countryEntity(row.label) : entityType ? { type: entityType, value: row.label, label: row.label } : undefined;
   const displayLabel = entityType === "country" && !row.aggregate && !row.unknown ? bankCountryName(row.label, language) : row.label;
   const severity = attention && !row.unknown && row.count >= attention.minCount ? attention.severity : undefined;
   return <tr key={index} tabIndex={onRowFocus ? 0 : undefined} data-highlighted={highlightedRow === index || undefined}
			onMouseEnter={() => onRowHover?.(index)} onMouseLeave={() => onRowHover?.(null)}
			onFocus={() => onRowFocus?.(index)} onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) onRowFocus?.(null); }}>
			<td><LegendLabel>{row.color && <Dot $color={row.color} aria-hidden="true" />}{entityType === "country" && !row.aggregate && !row.unknown && <CountryFlag code={row.label} variant="rectangle" />}{onEntity && isOverviewEntity(entity) ? <More type="button" aria-haspopup="dialog" onClick={() => onEntity(entity)}>{displayLabel}</More> : <span>{displayLabel}</span>}</LegendLabel></td><td title={t("bankAnalysis.declinesCount")}>{severity ? <AttentionBadge $severity={severity} title={t(severity === "critical" ? "bankAnalysis.highFrequency" : "bankAnalysis.requiresAttention")} aria-label={`${bankCount(row.count, language)} ${t("bankAnalysis.declinesAria")} — ${t(severity === "critical" ? "bankAnalysis.highFrequencyAria" : "bankAnalysis.requiresAttentionAria")}`}>{bankCount(row.count, language)}</AttentionBadge> : bankCount(row.count, language)}<small aria-hidden="true">{t("bankAnalysis.countUnit")}</small></td><td title={t("bankAnalysis.shareOfDeclines")}>{declineShare(row.count, total, language)}</td></tr>;
  })}</tbody>
	</Table>;
}

function DonutBreakdown({ rows, total, dimension = "reason", onEntity }: { rows: BankBreakdownRow[]; total: number; dimension?: "reason" | "country" | "bank"; onEntity?: (entity: OverviewEntity) => void }) {
	const { t, i18n } = useTranslation();
	const language = appLanguage(i18n.resolvedLanguage || i18n.language);
	const [expanded, setExpanded] = useState(false);
	const [hoveredRow, setHoveredRow] = useState<number | null>(null);
	const [focusedRow, setFocusedRow] = useState<number | null>(null);
	const highlightedRow = hoveredRow ?? focusedRow;
	const chart = useRef<HTMLDivElement>(null);
	const countries = dimension === "country", banks = dimension === "bank";
	const title = t(countries ? "bankAnalysis.countries" : banks ? "bankAnalysis.banks" : "bankAnalysis.reasons.title");
	const localizedRows = rows.map(row => ({ ...row, label: row.unknown
		? t(countries ? "bankAnalysis.countryNotProvided" : banks ? "bankAnalysis.bankNotProvided" : "bankAnalysis.notProvided")
		: dimension === "reason" && "key" in row && typeof row.key === "string" && !row.key.startsWith("raw:") ? t(`bankAnalysis.reasons.${row.key}`) : row.label }));
	const visible = expanded ? localizedRows : localizedRows.slice(0, 5);
	const chartRows: ChartRow[] = visible.map((row, index) => ({ ...row, color: row.unknown ? "#dce2eb" : COLORS[index % COLORS.length] }));
	const other = localizedRows.slice(visible.length).reduce((sum, row) => sum + row.count, 0);
	const missing = Math.max(0, total - localizedRows.reduce((sum, row) => sum + row.count, 0));
	if (other) chartRows.push({ label: t(countries ? "bankAnalysis.otherCountries" : banks ? "bankAnalysis.otherBanks" : "bankAnalysis.otherReasons"), count: other, color: "#a9b4c8", aggregate: true });
	if (missing) chartRows.push({ label: t("bankAnalysis.notProvided"), count: missing, color: "#dce2eb", aggregate: true });
	const chartData = chartRows.map(row => ({ ...row, name: countries && !row.aggregate && !row.unknown ? bankCountryName(row.label, language) : row.label }));
	return <Section aria-label={title}>
		<h3>{countries ? <Globe2 size={17} aria-hidden="true" /> : banks ? <Landmark size={17} aria-hidden="true" /> : <ChartPie size={17} aria-hidden="true" />}{title}</h3>
		{total > 0 ? <>
			<Chart ref={chart}>
				<PieChart width={180} height={180}>
					<Pie data={chartData} dataKey="count" nameKey="name" innerRadius={61} outerRadius={80} startAngle={90} endAngle={-270} stroke="none" isAnimationActive={false}
						shape={props => <HighlightedSector {...props} highlighted={highlightedRow === props.index} muted={highlightedRow !== null && highlightedRow !== props.index} />}>
						{chartRows.map((row, index) => <Cell key={index} fill={row.color} />)}
					</Pie>
					<Tooltip
						portal={typeof document === "undefined" ? undefined : document.body}
						wrapperStyle={{ position: "fixed", left: 0, top: 0, width: 0, height: 0, zIndex: 100010, pointerEvents: "none" }} isAnimationActive={false}
						content={({ active, payload }) => {
							const item = payload?.[0];
							if (!active || !item) return null;
							return <FloatingDonutTooltip anchor={chart} name={String(item.name)} color={item.payload.color} count={Number(item.value)} total={total} />;
						}}
					/>
				</PieChart>
				<Center><strong>{bankCount(total, language)}</strong><span>{t("bankAnalysis.totalDeclines")}</span></Center>
			</Chart>
			<Breakdown rows={chartRows} total={total} label={t(countries ? "bankAnalysis.country" : banks ? "bankAnalysis.bank" : "bankAnalysis.reasons.reason")} entityType={dimension === "reason" ? undefined : dimension} onEntity={onEntity}
				highlightedRow={highlightedRow} onRowHover={setHoveredRow} onRowFocus={setFocusedRow} />
			{rows.length > 5 && <More type="button" aria-expanded={expanded} onClick={() => { setHoveredRow(null); setFocusedRow(null); setExpanded(value => !value); }}>{expanded ? t("bankAnalysis.collapse") : t(countries ? "bankAnalysis.allCountries" : banks ? "bankAnalysis.allBanks" : "bankAnalysis.allReasons", { count: rows.length })}</More>}
		</> : <Note>{t("bankAnalysis.noPeriodDeclines")}</Note>}
	</Section>;
}

function RepeatedEntities({ rows, total, kind, policy, onEntity }: { rows: BankBreakdownRow[]; total: number; kind: "card" | "email"; policy: ReturnType<typeof repeatPolicy>; onEntity?: (entity: OverviewEntity) => void }) {
	const { t, i18n } = useTranslation();
	const language = appLanguage(i18n.resolvedLanguage || i18n.language);
	const [showAll, setShowAll] = useState(false);
	const result = analyzeRepeats(rows, policy);
	const visible = (showAll ? rows : result.attention).map(row => ({ ...row, label: row.unknown ? t(kind === "card" ? "bankAnalysis.cardNotProvided" : "bankAnalysis.emailNotProvided") : row.label }));
	const hiddenCount = rows.length - result.attention.length;
	const unknownCount = result.unknown.reduce((sum, row) => sum + row.count, 0);
	return <Details>
		<summary>{kind === "card" ? <CreditCard size={16} aria-hidden="true" /> : <Mail size={16} aria-hidden="true" />}
			{t(kind === "card" ? "bankAnalysis.cardsWithDeclines" : "bankAnalysis.emailsWithDeclines")} · {policy && result.attention.length > 0
				? <AttentionBadge $severity={policy.severity}>{t("bankAnalysis.attentionCount", { count: result.attention.length })}</AttentionBadge>
				: policy ? t("bankAnalysis.noThresholdItems") : t("bankAnalysis.periodUnknown")}
		</summary>
		<Note>{t("bankAnalysis.crmItems", { count: rows.length })} {policy ? t("bankAnalysis.attentionThreshold", { count: bankCount(policy.minCount, language), days: policy.days }) : t("bankAnalysis.periodThresholdUnavailable")}</Note>
		{visible.length > 0 ? <Breakdown rows={visible} total={total} label={t(kind === "card" ? "bankAnalysis.card" : "bankAnalysis.email")} entityType={kind === "email" ? "email" : undefined} onEntity={onEntity} attention={policy ?? undefined} />
			: policy && <Note>{t(kind === "card" ? "bankAnalysis.noCardsAtThreshold" : "bankAnalysis.noEmailsAtThreshold")}</Note>}
		{unknownCount > 0 && <Note>{t("bankAnalysis.unknownDeclines", { label: t(kind === "card" ? "bankAnalysis.cardNotProvided" : "bankAnalysis.emailNotProvided"), count: bankCount(unknownCount, language) })}</Note>}
		{hiddenCount > 0 && <More type="button" aria-expanded={showAll} onClick={() => setShowAll(value => !value)}>{showAll ? t("bankAnalysis.attentionOnly") : t("bankAnalysis.showFullTop", { count: rows.length })}</More>}
	</Details>;
}

export function BankAnalysisDetails({ data, onEntity }: { data: BankAnalysisDetailsData; onEntity?: (entity: OverviewEntity) => void }) {
	const { t, i18n } = useTranslation();
	const language = appLanguage(i18n.resolvedLanguage || i18n.language);
	const top = data.reasons[0];
	const topReason = top && !top.key.startsWith("raw:") ? t(`bankAnalysis.reasons.${top.key}`) : top?.label;
	const actionableReasons = new Set(["google_pay_token", "wrong_params", "wrong_pan", "no_channel", "fraud", "funds", "conditions", "security", "3ds", "pending"]);
	const summary = data.total === 0
		? { text: t("bankAnalysis.summary.noDeclines") }
		: !top
			? { text: t("bankAnalysis.summary.noBreakdown", { total: bankCount(data.total, language) }) }
			: {
				text: t("bankAnalysis.summary.topReason", { reason: topReason, count: bankCount(top.count, language), total: bankCount(data.total, language), share: declineShare(top.count, data.total, language) }),
				action: t(actionableReasons.has(top.key) ? `bankAnalysis.actions.${top.key}` : "bankAnalysis.actions.fallback"),
			};
	const policy = repeatPolicy(data.period);
	return <div>
		<Summary style={data.total === 0 ? { marginBottom: 0 } : undefined}>{data.total === 0 ? <Info size={20} aria-hidden="true" /> : <Lightbulb size={20} aria-hidden="true" />}<div><p><strong>{summary.text}</strong></p>{summary.action && <p>{summary.action}</p>}</div></Summary>
		{data.total > 0 && <Charts>
			<DonutBreakdown rows={data.reasons} total={data.total} />
			{data.countries.length > 0 && <DonutBreakdown rows={data.countries} total={data.total} dimension="country" onEntity={onEntity} />}
			{data.banks.length > 0 && <DonutBreakdown rows={data.banks} total={data.total} dimension="bank" onEntity={onEntity} />}
		</Charts>}
		{data.errorCount < data.total && <Note>{t("bankAnalysis.partialReasons", { count: bankCount(data.errorCount, language), total: bankCount(data.total, language) })}</Note>}
		{data.cards.length > 0 && <RepeatedEntities key={`cards:${data.period.from}:${data.period.to}`} rows={data.cards} total={data.total} kind="card" policy={policy} />}
		{data.emails.length > 0 && <RepeatedEntities key={`emails:${data.period.from}:${data.period.to}`} rows={data.emails} total={data.total} kind="email" policy={policy} onEntity={onEntity} />}
	</div>;
}
